import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {DigitalCaseflowLog} from '@secca/shared/models/digital-caseflow-log';
import {DigitalCaseflowActivityLog} from '@secca/shared/models/digital-caseflow-activity-log';
import {TranslateService} from '@ngx-translate/core';
import {DigitalCaseflowActivityErrorLog} from '@secca/shared/models/digital-caseflow-activity-error-log';
import {DigitalCaseflowActivityTableEntry} from '@secca/shared/models/digital-caseflow-activity-table-entry';

import {DigitalCaseflowService} from '@secca/core/services/digital-caseflow.service';
import {take} from 'rxjs/operators';

@Component({
    selector: 'app-case-summary-digital-caseflow-details',
    templateUrl: './case-summary-digital-caseflow-details.component.html',
    styleUrls: ['./case-summary-digital-caseflow-details.component.scss'],
    standalone: false
})
export class CaseSummaryDigitalCaseflowDetailsComponent implements OnInit {
  @Input()
  flowLog: DigitalCaseflowLog;

  @Output()
  refreshed = new EventEmitter<void>();

  translateService: TranslateService;
  digitalCaseflowService: DigitalCaseflowService;

  validationTableEntries = new Array<DigitalCaseflowActivityTableEntry>();
  activityTableEntries = new Array<DigitalCaseflowActivityTableEntry>();

  errorCodeTranslationPrefix = 'digital-caseflow-activity-log-error-';
  activityTypeTranslationPrefix = 'digital-caseflow-activity-action-';
  resultTranslationPrefix = 'digital-caseflow-activity-result-';

  resuming = false;
  flowExecuted = false;
  executionError: string = null;

  constructor(translateService: TranslateService, digitalCaseflowService: DigitalCaseflowService) {
    this.translateService = translateService;
    this.digitalCaseflowService = digitalCaseflowService;
  }

  ngOnInit() {
    this.validationTableEntries = new Array<DigitalCaseflowActivityTableEntry>();
    this.activityTableEntries = new Array<DigitalCaseflowActivityTableEntry>();
    const validationActivities = this.flowLog.activityLogs.filter(this.isValidationActivity);
    const actionActivities = this.flowLog.activityLogs.filter(this.isActionActivity);

    for (const activity of validationActivities) {
      const activityName = activity.activityName;
      for (const errorLog of activity.validationErrorLogs) {
        const activityMessage = errorLog.errorMessage;
        const activityResult = this.translateActivityResult(activity);

        const validationEntry = new DigitalCaseflowActivityTableEntry(activityName, activityMessage, activityResult);
        this.validationTableEntries.push(validationEntry);
      }
    }

    for (const activity of actionActivities) {
      const activityName = activity.activityName;
      if (activity.result === 'SUCCESS') {
        const activityResult = this.translateActivityResult(activity);

        const activityEntry = new DigitalCaseflowActivityTableEntry(activityName, '', activityResult);
        this.activityTableEntries.push(activityEntry);
      } else {
        for (const errorLog of activity.activityErrorLogs) {
          const activityMessage = this.translateErrorCode(errorLog);
          const activityResult = this.translateActivityResult(activity);

          const activityEntry = new DigitalCaseflowActivityTableEntry(activityName, activityMessage, activityResult);
          this.activityTableEntries.push(activityEntry);
        }
      }
    }
  }

  isValidationActivity(activityLog: DigitalCaseflowActivityLog): boolean {
    return activityLog.activityType === 'VALIDATION';
  }

  isActionActivity(activityLog: DigitalCaseflowActivityLog): boolean {
    return activityLog.activityType !== 'VALIDATION';
  }

  translateErrorCode(errorLog: DigitalCaseflowActivityErrorLog): string {
    const errorCodeWithPrefix = this.errorCodeTranslationPrefix + errorLog.errorCode;
    return this.translateService.instant(errorCodeWithPrefix);
  }

  translateActivityType(activityType: string): string {
    const activityTypeWithPrefix = this.activityTypeTranslationPrefix + activityType;
    return this.translateService.instant(activityTypeWithPrefix);
  }

  translateActivityResult(activityLog: DigitalCaseflowActivityLog): string {
    const resultCodeWithPrefix = this.resultTranslationPrefix + activityLog.result;
    return this.translateService.instant(resultCodeWithPrefix);
  }

  getTranslatedErrorCodes(activityLog: DigitalCaseflowActivityLog): string {
    return activityLog.activityErrorLogs.map(item => this.translateService.instant(this.errorCodeTranslationPrefix + item.errorCode)).join(',');
  }

  getTranslatedResult(activityLog: DigitalCaseflowActivityLog): string {
    return this.translateService.instant(this.resultTranslationPrefix + activityLog.result);
  }

  canResume(): boolean {
    if (this.resuming || !this.flowLog || !this.flowLog.activityLogs || this.flowLog.activityLogs.length === 0) {
      return false;
    }
    const actionActivities = this.flowLog.activityLogs.filter(activity => this.isActionActivity(activity));
    if (actionActivities.length === 0) {
      return false;
    }
    const lastActionActivity = actionActivities[actionActivities.length - 1];
    return lastActionActivity.result === 'FAILED';
  }

  resumeFlow() {
    this.resuming = true;
    this.flowExecuted = false;
    this.executionError = null;
    // Subscribe to completion before resuming to ensure we don't miss the event
    this.digitalCaseflowService.subscribeToCompletion(this.flowLog.id)
      .pipe(take(1))
      .subscribe({
        next: (reserveEstimate: any) => {
          this.digitalCaseflowService.getDigitalCaseflowLog(this.flowLog.id).subscribe(
            updatedLog => {
              this.flowLog = updatedLog;
              this.resuming = false;
              this.flowExecuted = true;
              this.ngOnInit();
              this.refreshed.emit();
            }
          );
        },
        error:(error: any) => {
          console.error('Error waiting for flow completion', error);
          this.executionError = error;
          // Fallback refresh in case of SSE error
          this.digitalCaseflowService.getDigitalCaseflowLog(this.flowLog.id).subscribe(
            updatedLog => {
              this.flowLog = updatedLog;
              this.resuming = false;
              this.flowExecuted = true;
              this.ngOnInit();
              this.refreshed.emit();
            }
          );
        }
      });

    this.digitalCaseflowService.resumeDigitalCaseflow(this.flowLog.id)
      .subscribe(
        () => console.log('Flow resumption initiated'),
        error => {
          console.log(error);
          this.resuming = false;
        }
      );
  }
}
